/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 07-07-2026
 *
 * Abstracte basis voor een groep van hover-gestuurde overlays rond één node
 * (bijvoorbeeld uitleg-tooltip samen met de feedback-iconen). Regelt de
 * gedeelde hover-coördinatie in abstracte termen; een concrete subklasse levert
 * de structuur (vaste kind-componenten) en de zichtbaarheidsregels per lid.
 *
 * Vervangt de ng1 `node-dom`-machinerie (`iconMouseOver`/`selfMouseOver`/
 * `selfMouseOverNaIconMouseOver`/`subMouseover` + gecombineerde `containerRect`):
 * omdat alle leden echte DOM-kinderen van één host zijn, volstaat één
 * enter/leave-paar op de host, en "één node-groep tegelijk" volgt uit het
 * hover-signaal van het canvas (cytoscape hovert maar één node).
 */
import {computed, Directive, effect, inject, signal, untracked} from '@angular/core';
import {KeyedTimeout} from 'sihw-ngx';
import {BaseNodeGekoppeldComponent} from './base-node-gekoppeld.component';
import {DlModelCanvasComponent} from '../canvas/dl-modelcanvas/dl-modelcanvas.component';

/**
 * De groep waarvan de ballon vooraan mag staan; er is er telkens hoogstens één. Meerdere ballonnen
 * kunnen tegelijk open zijn (een vastgeklikte bouwsuggestie, een geforceerde harde regel, of
 * normfeedback op een andere node), en zonder deze claim komen die groepen op dezelfde z-index uit.
 * Dan beslist de DOM-volgorde — die van `cy.nodes()` — en vallen de lampjes van een willekeurige
 * buurnode alsnog over de ballon heen. De laatst geopende ballon claimt de voorgrond.
 */
const ballonVoorgrond = signal<BaseNodeOverlayGroepComponent | null>(null);

@Directive()
export abstract class BaseNodeOverlayGroepComponent extends BaseNodeGekoppeldComponent {

    /**
     * Het canvas ("baas") dat de globale hover-state en model-façades levert.
     */
    protected readonly modelcanvas = inject(DlModelCanvasComponent, {host: true});

    /**
     * Of de muis op dit moment binnen de groep-DOM (bv. de uitleg) zit.
     */
    protected readonly zelfGehoverd = signal(false);

    /**
     * Welk sub-element van de groep momenteel de muis boven zich heeft
     * (bv. `'bouwsuggestie'`), of `null`. Kinderen melden dit via meldSub/wisSub.
     */
    protected readonly actieveSub = signal<string | null>(null);

    /**
     * De sub-elementen die op dit moment een geopende ballon tonen. Anders dan `actieveSub` blijft
     * deze verzameling staan zolang de ballon open is: een vastgeklikte ballon of een ballon die
     * door een harde regel geforceerd is, overleeft het aflopen van de sluitvertraging.
     */
    private readonly openSubs = signal<ReadonlySet<string>>(new Set());

    /**
     * Effectieve activatie van de groep, mét sluitvertraging zodat de muis van de
     * node naar de groep-inhoud kan bewegen zonder dat de groep dichtklapt.
     */
    protected readonly actief = signal(false);

    /**
     * Of de groep voor de overlays van andere nodes langs getekend moet worden: bij hover, en
     * zolang er een ballon open staat. Elke node-volger is door zijn transform een eigen stacking
     * context, dus de z-index van de ballon zelf komt niet buiten de groep — alleen de groep-div
     * kan omhoog.
     */
    protected readonly naarVoren = computed(() => this.actief() || this.openSubs().size > 0);

    /**
     * Of deze groep de ballon-voorgrond heeft. Dat weegt zwaarder dan hover: een ballon klapt naar
     * boven uit, over het gebied van de node erboven, en die node kan tegelijk als gehoverd gemeld
     * zijn (cytoscape hovert op de node, de ballon hangt in andermans vak). Zonder dit onderscheid
     * wint bij snel bewegen de hover van de bovenliggende node en schijnen zijn lampjes door de
     * ballon heen.
     */
    protected readonly heeftOpenBallon = computed(() => ballonVoorgrond() === this);

    /**
     * De sluitvertraging van de groep ('sluit')
     */
    private readonly timers = new KeyedTimeout();

    constructor(loglevel?: string) {
        super(loglevel);
        // De groep is actief zolang de node gehoverd is óf de muis binnen de
        // groep-DOM zit. Bij wegvallen volgt een korte vertraging.
        effect(() => {
            const gehoverd = this.modelcanvas.gehoverdeNodeId() === this.node?.id();
            const zelf = this.zelfGehoverd();
            untracked(() => this.zetActief(gehoverd || zelf));
        });
        // Zodra deze groep een ballon toont claimt hij de voorgrond; bij het sluiten geeft hij die
        // weer vrij, tenzij een andere groep de claim inmiddels heeft overgenomen.
        effect(() => {
            const toontBallon = this.openSubs().size > 0;
            untracked(() => {
                if (toontBallon) {
                    ballonVoorgrond.set(this);
                } else if (ballonVoorgrond() === this) {
                    ballonVoorgrond.set(null);
                }
            });
        });
    }

    /**
     * Meldt dat een sub-element van de groep de muis boven zich heeft.
     */
    protected meldSub(sub: string): void {
        this.actieveSub.set(sub);
        this.openSubs.update(subs => new Set(subs).add(sub));
    }

    /**
     * Wist de sub-melding, mits die het opgegeven sub-element betreft.
     */
    protected wisSub(sub: string): void {
        if (this.actieveSub() === sub) this.actieveSub.set(null);
        this.openSubs.update(subs => {
            const nieuw = new Set(subs);
            nieuw.delete(sub);
            return nieuw;
        });
    }

    /**
     * Muis komt de groep-DOM binnen.
     */
    protected opGroepEnter(): void {
        this.zelfGehoverd.set(true);
    }

    /**
     * Muis verlaat de groep-DOM.
     */
    protected opGroepLeave(): void {
        this.zelfGehoverd.set(false);
    }

    /**
     * Ruimt de sluittimer op bij verwijdering, en geeft een eventuele voorgrond-claim vrij zodat een
     * verwijderde node die niet blijft vasthouden.
     */
    override ngOnDestroy(): void {
        this.timers.clearAll();
        if (ballonVoorgrond() === this) {
            ballonVoorgrond.set(null);
        }
        super.ngOnDestroy();
    }

    /**
     * Zet de actief-state met sluitvertraging: direct aan, vertraagd uit.
     */
    private zetActief(aan: boolean): void {
        if (aan) {
            this.timers.clear('sluit');
            this.actief.set(true);
        } else if (this.actief() && !this.timers.check('sluit')) {
            this.timers.set('sluit', () => {
                this.zelfGehoverd.set(false);
                this.actieveSub.set(null);
                this.actief.set(false);
            }, 120);
        }
    }
}
